import { motion } from 'motion/react'
import { useInView } from 'motion/react'
import { useRef, useState } from 'react'
import { fontSans, fontMono, hexToRgba, SENTINEL_DEMO_URL } from './theme'

const ACCENT = '#2DD4BF'

const entityColors: Record<string, string> = {
  EMAIL: '#93c5fd',
  PHONE: '#86efac',
  CARD: '#fcd34d',
  AADHAAR: '#67e8f9',
  AWS_KEY: '#fca5a5',
  IP: '#c4b5fd',
}

function ScanVisual() {
  const [scanned, setScanned] = useState(false)
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })

  // Fire the "scan" shortly after the section scrolls into view
  const started = useRef(false)
  if (inView && !started.current) {
    started.current = true
    setTimeout(() => setScanned(true), 700)
  }

  const sample =
    'Hi, I am Priya Sharma (priya.s@acme-corp.com, +91 98450 12345). ' +
    'Card on file 4111 1111 1111 1111, ID 2341 8875 2193, ' +
    'deploy key AKIAIOSFODNN7EXAMPLE from 103.21.44.9.'

  const entities: Array<{ text: string; type: keyof typeof entityColors }> = [
    { text: 'priya.s@acme-corp.com', type: 'EMAIL' },
    { text: '+91 98450 12345', type: 'PHONE' },
    { text: '4111 1111 1111 1111', type: 'CARD' },
    { text: '2341 8875 2193', type: 'AADHAAR' },
    { text: 'AKIAIOSFODNN7EXAMPLE', type: 'AWS_KEY' },
    { text: '103.21.44.9', type: 'IP' },
  ]

  const renderSample = () => {
    if (!scanned) return <span>{sample}</span>
    let remaining = sample
    const parts: React.ReactNode[] = []
    let key = 0
    while (remaining.length) {
      let earliest = Infinity
      let found: (typeof entities)[number] | null = null
      for (const e of entities) {
        const idx = remaining.indexOf(e.text)
        if (idx !== -1 && idx < earliest) {
          earliest = idx
          found = e
        }
      }
      if (!found) {
        parts.push(<span key={key++}>{remaining}</span>)
        break
      }
      if (earliest > 0) parts.push(<span key={key++}>{remaining.slice(0, earliest)}</span>)
      parts.push(
        <mark
          key={key++}
          style={{
            background: hexToRgba(entityColors[found.type], 0.16),
            color: entityColors[found.type],
            borderRadius: '4px',
            padding: '1px 4px',
            fontWeight: 600,
          }}
        >
          {found.text}
        </mark>,
      )
      remaining = remaining.slice(earliest + found.text.length)
    }
    return parts
  }

  const steps = ['Ingest', 'Detect', 'Mask', 'Audit']
  const activeStep = scanned ? 2 : 1

  return (
    <div
      ref={ref}
      style={{
        background:
          'linear-gradient(160deg, rgba(45,212,191,0.05), rgba(10,15,31,0.4))',
        border: `1px solid ${hexToRgba(ACCENT, 0.25)}`,
        borderRadius: '20px',
        padding: '1.75rem',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '-60px',
          left: '-60px',
          width: '220px',
          height: '220px',
          background: `radial-gradient(circle, ${hexToRgba(ACCENT, 0.14)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      {/* stepper */}
      <div
        style={{
          display: 'flex',
          gap: '6px',
          flexWrap: 'wrap',
          marginBottom: '1.1rem',
        }}
      >
        {steps.map((s, i) => {
          const done = i < activeStep
          const current = i === activeStep
          return (
            <div
              key={s}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                fontSize: '0.74rem',
                fontWeight: 600,
                fontFamily: fontSans,
                padding: '6px 13px 6px 8px',
                borderRadius: '100px',
                color: done || current ? ACCENT : 'rgba(255,255,255,0.4)',
                background:
                  done || current ? hexToRgba(ACCENT, 0.1) : 'rgba(255,255,255,0.04)',
                border: `1px solid ${
                  done || current ? hexToRgba(ACCENT, 0.35) : 'rgba(255,255,255,0.08)'
                }`,
                transition: 'all 0.4s ease',
              }}
            >
              <span
                style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  background: done
                    ? ACCENT
                    : current
                      ? hexToRgba(ACCENT, 0.3)
                      : 'rgba(255,255,255,0.08)',
                  color: done ? '#06251f' : 'inherit',
                }}
              >
                {done ? '✓' : i + 1}
              </span>
              {s}
            </div>
          )
        })}
      </div>

      {/* scan target */}
      <div
        style={{
          background: 'rgba(10,15,31,0.65)',
          border: '1px solid rgba(255,255,255,0.09)',
          borderRadius: '14px',
          padding: '16px 18px',
          marginBottom: '1rem',
        }}
      >
        <div
          style={{
            fontFamily: fontMono,
            fontSize: '0.66rem',
            color: 'rgba(255,255,255,0.35)',
            marginBottom: '8px',
            letterSpacing: '0.04em',
          }}
        >
          PROMPT / FREE TEXT — scanning entirely in your browser
        </div>
        <div
          style={{
            fontFamily: fontMono,
            fontSize: '0.78rem',
            lineHeight: 2,
            color: 'rgba(255,255,255,0.75)',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {renderSample()}
        </div>
        {!scanned && (
          <div
            className="sentinel-scanline"
            style={{
              height: '2px',
              marginTop: '10px',
              borderRadius: '100px',
              background: `linear-gradient(90deg, transparent, ${ACCENT}, transparent)`,
            }}
          />
        )}
      </div>

      {/* masking before/after */}
      <div
        style={{
          background: 'rgba(10,15,31,0.65)',
          border: '1px solid rgba(255,255,255,0.09)',
          borderRadius: '14px',
          padding: '14px 18px',
        }}
      >
        <div
          style={{
            fontFamily: fontMono,
            fontSize: '0.66rem',
            color: 'rgba(255,255,255,0.35)',
            marginBottom: '10px',
            letterSpacing: '0.04em',
          }}
        >
          MASKED FOR EGRESS — hash · partial · redact
        </div>
        {[
          { before: 'priya.s@acme-corp.com', after: 'p••••@a••••.com', strat: 'partial' },
          { before: '4111 1111 1111 1111', after: 'f3a9c21b…', strat: 'hash' },
          { before: '2341 8875 2193', after: '████████████', strat: 'redact' },
        ].map((r) => (
          <div
            key={r.before}
            className="sentinel-maskrow"
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr) auto',
              gap: '10px',
              alignItems: 'center',
              padding: '7px 0',
              borderBottom: '1px solid rgba(255,255,255,0.05)',
              fontFamily: fontMono,
              fontSize: '0.74rem',
              opacity: scanned ? 1 : 0.35,
              transition: 'opacity 0.6s ease 0.3s',
            }}
          >
            <span
              style={{
                color: 'rgba(255,255,255,0.45)',
                overflowWrap: 'anywhere',
              }}
            >
              {r.before}
            </span>
            <span style={{ color: ACCENT, overflowWrap: 'anywhere' }}>
              {r.after}
            </span>
            <span
              style={{
                fontSize: '0.62rem',
                color: 'rgba(255,255,255,0.4)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '100px',
                padding: '2px 9px',
                textAlign: 'center',
              }}
            >
              {r.strat}
            </span>
          </div>
        ))}
      </div>

      {/* risk summary */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginTop: '1rem',
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            fontFamily: fontSans,
            fontSize: '0.72rem',
            color: 'rgba(255,255,255,0.4)',
          }}
        >
          risk
        </span>
        {[
          { label: '6 entities', w: '38%', c: '#F87171' },
          { label: '2 medium', w: '22%', c: '#F5B850' },
          { label: '0 low', w: '8%', c: '#94a3b8' },
        ].map((b) => (
          <div
            key={b.label}
            style={{
              height: '7px',
              width: b.w,
              minWidth: '34px',
              borderRadius: '100px',
              background: 'rgba(255,255,255,0.06)',
              overflow: 'hidden',
              flexGrow: 1,
            }}
            title={b.label}
          >
            <div
              style={{
                height: '100%',
                width: scanned ? '100%' : '0%',
                background: b.c,
                borderRadius: '100px',
                transition: 'width 0.8s ease 0.5s',
              }}
            />
          </div>
        ))}
        <span
          style={{
            fontFamily: fontSans,
            fontSize: '0.72rem',
            fontWeight: 600,
            color: '#F87171',
          }}
        >
          HIGH — mask before this reaches a model
        </span>
      </div>
    </div>
  )
}

const featureRows = [
  {
    title: '25+ PII entity types',
    body: 'Emails, phones, SSNs, cards, IPs and names — plus locale identities like Aadhaar, IBAN, My Number, NRIC and CPF, and leaked secrets like JWTs, private keys and AWS/GitHub tokens.',
  },
  {
    title: 'Built for AI pipelines',
    body: 'Scan datasets before they hit a vector store. Scan prompts and free text before they hit a model. Sentinel is the compliance layer for data that now flows to third-party LLMs.',
  },
  {
    title: 'Masking you control',
    body: 'Choose per column: salted hash, partial mask or full redaction. Preview the masked output, then export the exact code that produced it.',
  },
  {
    title: 'Evidence, not vibes',
    body: 'Risk scoring and audit-ready exports for GDPR, India DPDP and the EU AI Act — the answer to "what PII went to which model vendor?"',
  },
]

export function Sentinel() {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section
      id="sentinel"
      style={{
        padding: '7rem 2rem',
        background:
          'linear-gradient(180deg, #0A0F1F 0%, #0a1a20 50%, #0A0F1F 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: `linear-gradient(90deg, transparent, ${hexToRgba(ACCENT, 0.4)}, transparent)`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '10%',
          right: '-200px',
          width: '500px',
          height: '500px',
          background: `radial-gradient(circle, ${hexToRgba(ACCENT, 0.06)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <div style={{ maxWidth: '1280px', margin: '0 auto', position: 'relative' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '4rem',
            alignItems: 'center',
          }}
        >
          {/* Visual side — left this time for rhythm */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <ScanVisual />
          </motion.div>

          {/* Narrative side */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <span
              style={{
                fontFamily: fontSans,
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: ACCENT,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '1rem',
                padding: '5px 14px',
                borderRadius: '100px',
                background: hexToRgba(ACCENT, 0.08),
                border: `1px solid ${hexToRgba(ACCENT, 0.3)}`,
              }}
            >
              Sentinel · Free Standalone Release
            </span>
            <h2
              style={{
                fontFamily: fontSans,
                fontSize: 'clamp(1.9rem, 3.6vw, 2.9rem)',
                fontWeight: 800,
                color: '#ffffff',
                letterSpacing: '-0.03em',
                lineHeight: 1.12,
                marginBottom: '1.25rem',
              }}
            >
              Know exactly what PII{' '}
              <span
                style={{
                  background: `linear-gradient(135deg, ${ACCENT}, #4A90E2)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                leaves your data.
              </span>
            </h2>
            <p
              style={{
                fontFamily: fontSans,
                fontSize: '1.02rem',
                color: 'rgba(255,255,255,0.55)',
                lineHeight: 1.75,
                marginBottom: '2rem',
              }}
            >
              Data now flows into third-party LLMs, RAG pipelines and vector
              databases — a governance surface that didn&apos;t exist when
              enterprise privacy tools were built. Sentinel scans it, masks it,
              and proves it.{' '}
              <span style={{ color: 'rgba(255,255,255,0.85)' }}>
                And the scanner is yours, free — no platform required.
              </span>
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.4rem' }}>
              {featureRows.map((f, i) => (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 16 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.15 + i * 0.1 }}
                  style={{ display: 'flex', gap: '14px' }}
                >
                  <div
                    style={{
                      width: '26px',
                      height: '26px',
                      borderRadius: '8px',
                      background: hexToRgba(ACCENT, 0.1),
                      border: `1px solid ${hexToRgba(ACCENT, 0.3)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      marginTop: '2px',
                      color: ACCENT,
                    }}
                  >
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M4 8.2 6.5 10.7 12 5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <div
                      style={{
                        fontFamily: fontSans,
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        color: 'rgba(255,255,255,0.92)',
                        marginBottom: '3px',
                      }}
                    >
                      {f.title}
                    </div>
                    <div
                      style={{
                        fontFamily: fontSans,
                        fontSize: '0.85rem',
                        color: 'rgba(255,255,255,0.5)',
                        lineHeight: 1.65,
                      }}
                    >
                      {f.body}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div
              style={{
                display: 'flex',
                gap: '1rem',
                marginTop: '2.25rem',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <a
                href={SENTINEL_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontFamily: fontSans,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: '#06251f',
                  textDecoration: 'none',
                  padding: '0.75rem 1.6rem',
                  borderRadius: '10px',
                  background: `linear-gradient(135deg, ${ACCENT}, #20A39E)`,
                  boxShadow: `0 0 30px ${hexToRgba(ACCENT, 0.3)}`,
                  transition: 'all 0.25s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = `0 8px 36px ${hexToRgba(ACCENT, 0.45)}`
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = `0 0 30px ${hexToRgba(ACCENT, 0.3)}`
                }}
              >
                Launch the Free Demo
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M6 3h7v7M13 3 6.5 9.5M11 10v3H3V5h3"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
              <a
                href="#platform"
                style={{
                  fontFamily: fontSans,
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.55)',
                  textDecoration: 'none',
                  borderBottom: `1px dashed ${hexToRgba(ACCENT, 0.4)}`,
                  paddingBottom: '2px',
                }}
              >
                Or use Sentinel inside the SynKrasis platform →
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      <style>{`
        @keyframes sentinelScan {
          0% { opacity: 0.2; transform: scaleX(0.3); }
          50% { opacity: 1; }
          100% { opacity: 0.2; transform: scaleX(1); }
        }
        .sentinel-scanline {
          animation: sentinelScan 1.1s ease-in-out infinite;
          transform-origin: left center;
        }
        @media (max-width: 480px) {
          .sentinel-maskrow {
            grid-template-columns: minmax(0, 1fr) auto !important;
          }
          .sentinel-maskrow span:nth-child(2)::before {
            content: '→ ';
            color: rgba(255,255,255,0.3);
          }
          .sentinel-maskrow span:first-child {
            grid-column: 1 / -1;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .sentinel-scanline { animation: none; }
        }
      `}</style>
    </section>
  )
}
